import React, { useMemo } from "react";

import { faInfoCircle, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";

import { ErrorHint } from "src/base-components/ErrorHint";
import { Icon } from "src/base-components/Icon";
import { Label } from "src/base-components/Label";
import { Pill } from "src/base-components/Pill";
import { Option, Section, Select } from "src/base-components/Select";
import { EventTypesSelector } from "src/cases/templates/caseBlocks/EventTypesSelector";
import {
  BlockFieldName,
  DECISION_FLOW_MAPPING_KEY,
  useBlockInputOptions,
} from "src/cases/templates/caseBlocks/useBlockInputOptions";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import {
  LinkedBlockConfigAttribute,
  LinkedBlockConfigAttributeKind,
} from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";

const COMPOSITE_KEY_SEPARATOR = "#";

const createCompositeKey = (
  key: string | null | undefined,
  kind: LinkedBlockConfigAttribute["kind"],
): string => {
  if (kind === LinkedBlockConfigAttributeKind.ADDITIONAL_INPUTS) {
    return DECISION_FLOW_MAPPING_KEY;
  }

  const keyStr = key ?? "";
  return `${keyStr}${COMPOSITE_KEY_SEPARATOR}${kind}`;
};

const parseCompositeKey = (
  compositeKey: string,
): { key: string | null; kind: LinkedBlockConfigAttribute["kind"] } => {
  const [keyStr, kind] = compositeKey.split(COMPOSITE_KEY_SEPARATOR);

  return {
    key: keyStr === "null" || !keyStr ? null : keyStr,
    kind: kind as LinkedBlockConfigAttribute["kind"],
  };
};

const getPropertyTypeDescription = (blockKind?: string): string => {
  if (!blockKind) {
    return "properties";
  }

  switch (blockKind) {
    case "entity_details":
    case "related_cases":
    case "related_entities":
    case "related_events":
      return "entity reference properties";
    case "event_details":
      return "event reference properties";
    case "case_details":
    case "markdown":
    default:
      return "string properties";
  }
};

type BlockConfigFormByKindProps = {
  blockIndex: number;
};

type BlockInputFieldName =
  `attributes.case_blocks_configs.${number}.config.${BlockFieldName}`;

type BlockInputFieldProps = {
  blockIndex: number;
  options: Option[];
  sections: Section[];
  configFieldName: BlockFieldName;
  blockKind: string;
};

const BlockInputField: React.FC<BlockInputFieldProps> = ({
  blockIndex,
  options,
  sections,
  configFieldName,
  blockKind,
}) => {
  const { control } = useFormContext<CaseTemplateFormValues>();
  const hasPermission = useCheckFieldPermission();

  const fieldName =
    `attributes.case_blocks_configs.${blockIndex}.config.${configFieldName}` as BlockInputFieldName;
  const canEditConfig = hasPermission("edit", fieldName);

  const enhancedSections = useMemo<Section[]>(
    () =>
      sections.map((section) => ({
        ...section,
        displayValue: getBlockInputSectionDisplayValue({
          blockKind,
          sectionKey: section.key,
          displayValue: section.displayValue,
        }),
      })),
    [sections, blockKind],
  );

  const enhancedOptions = useMemo<Option[]>(
    () =>
      options.map((option) => {
        if (option.key === DECISION_FLOW_MAPPING_KEY) {
          return {
            ...option,
            value: (
              <div className="flex w-full items-center justify-between">
                <span>Map this field in Decision Flow</span>
                <Tooltip
                  title="This input can be mapped later when configuring the Create Case node in the Decision Flow."
                  asChild
                >
                  <Icon color="text-gray-400" icon={faInfoCircle} size="sm" />
                </Tooltip>
              </div>
            ),
            searchText: "Map this field in Decision Flow",
          };
        }

        if (option.icon) {
          const { icon, ...optionWithoutIcon } = option;
          return {
            ...optionWithoutIcon,
            value: (
              <Pill size="sm" variant="gray">
                <Pill.Icon icon={icon} />
                <Pill.Text>{option.value}</Pill.Text>
              </Pill>
            ),
            searchText:
              typeof option.value === "string" ? option.value : undefined,
          };
        }

        return option;
      }),
    [options],
  );

  return (
    <div className="flex flex-col">
      <Label required>Block Input</Label>
      <Controller
        control={control}
        name={fieldName}
        render={({ field, fieldState }) => {
          const { value, onChange } = field as {
            value: LinkedBlockConfigAttribute | null;
            onChange: (val: LinkedBlockConfigAttribute | null) => void;
          };
          const { error: fieldError } = fieldState;

          const handleChange = (compositeKey?: string) => {
            if (!compositeKey) {
              onChange(null);
              return;
            }

            const { key, kind } = parseCompositeKey(compositeKey);
            const normalizedValue: LinkedBlockConfigAttribute =
              kind === LinkedBlockConfigAttributeKind.ADDITIONAL_INPUTS &&
              key === null
                ? { kind }
                : { kind, key };

            onChange(normalizedValue);
          };

          return (
            <>
              <Select
                dataLoc="block-input-select"
                disabled={!canEditConfig}
                dropdownPlaceholder={
                  enhancedOptions.length === 0 ? (
                    <EmptyState
                      description={`This block supports only ${getPropertyTypeDescription(
                        blockKind,
                      )}, but none are defined in this template. Please make sure the appropriate property is added.`}
                      headline="No compatible properties found"
                      icon={faSearch}
                      size="sm"
                    />
                  ) : undefined
                }
                errored={Boolean(fieldError)}
                options={enhancedOptions}
                placeholder="Select input"
                sections={enhancedSections}
                value={value ? createCompositeKey(value.key, value.kind) : ""}
                fullWidth
                sectionDividers
                onChange={handleChange}
              />
              {fieldError?.message && (
                <ErrorHint>{fieldError.message}</ErrorHint>
              )}
            </>
          );
        }}
      />
    </div>
  );
};

export const BlockConfigFormByKind: React.FC<BlockConfigFormByKindProps> = ({
  blockIndex,
}) => {
  const { options, sections, shouldShowInput, configFieldName, blockKind } =
    useBlockInputOptions(blockIndex);

  if (!blockKind) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {shouldShowInput && (
        <BlockInputField
          key={blockKind}
          blockIndex={blockIndex}
          blockKind={blockKind}
          configFieldName={configFieldName}
          options={options}
          sections={sections}
        />
      )}
      {blockKind === "related_events" && (
        <EventTypesSelector blockIndex={blockIndex} />
      )}
    </div>
  );
};

const getBlockInputSectionDisplayValue = ({
  sectionKey,
  displayValue,
  blockKind,
}: {
  sectionKey: string;
  displayValue: React.ReactNode;
  blockKind: string;
}) => {
  if (sectionKey === "properties" && blockKind === "case_details") {
    return (
      <div className="flex items-center justify-between pr-4 pb-1.5 pl-4">
        <span className="font-inter-semibold-13px">Properties</span>
        <Tooltip title="You can reference another case using its case ID.">
          <Icon color="text-gray-400" icon={faInfoCircle} size="sm" />
        </Tooltip>
      </div>
    );
  }

  return displayValue;
};
